import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { appointmentAPI, adminAPI, documentAPI, hipaaAPI, intakeAPI, labAPI } from '../../lib/api';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  User,
  Users,
  CaretLeft,
  CaretRight,
  Stethoscope,
  Phone,
  Envelope,
  CheckCircle,
  XCircle,
  HourglassHigh,
  Funnel,
  FileText,
  Shield,
  CreditCard,
  IdentificationCard,
  Heart,
  FirstAid,
  Warning,
  CaretDown,
  CaretUp,
  ArrowRight,
  ClipboardText,
  Pill,
  Flask,
  PencilSimple,
  Robot,
  VideoCamera,
  File,
  Image,
  FilePdf,
  DownloadSimple
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function DailySchedulePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [appointments, setAppointments] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('all');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, scheduled: 0, completed: 0, cancelled: 0 });
  const [expandedAppointment, setExpandedAppointment] = useState(null);
  const [patientDetails, setPatientDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Load patient details when appointment is expanded
  const loadPatientDetails = async (appointment) => {
    if (!appointment.patient_id) {
      toast.error('Patient ID not available for this appointment');
      return;
    }
    
    setLoadingDetails(true);
    try {
      const [docsRes, consentRes, intakeRes, labsRes] = await Promise.all([
        documentAPI.getPatientDocs(appointment.patient_id, true).catch(() => ({ data: [] })),
        hipaaAPI.getPatientStatus(appointment.patient_id).catch(() => ({ data: {} })),
        intakeAPI.getByPatient(appointment.patient_id).catch(() => ({ data: [] })),
        labAPI.getPatientLabs(appointment.patient_id).catch(() => ({ data: [] }))
      ]);
      
      // Organize documents by type - get the most recent of each type
      // Documents are sorted by uploaded_at desc from API, so first match is most recent
      const docs = docsRes.data || [];
      
      // Helper to find most recent active document of a type
      // Backend uses "status" field: "active" or "inactive"
      const findDoc = (type) => docs.find(d => 
        d.document_type === type && (d.status === 'active' || !d.status)
      );
      
      const insuranceFront = findDoc('insurance_front');
      const insuranceBack = findDoc('insurance_back');
      const licenseFront = findDoc('drivers_license_front');
      const licenseBack = findDoc('drivers_license_back');
      
      // Get medical documents (X-rays, MRIs, lab results, other uploads)
      const medicalDocs = docs.filter(d => 
        ['xray', 'mri', 'lab_result', 'medical_image', 'other'].includes(d.document_type)
      );
      
      // Get the most recent intake
      const intakes = intakeRes.data || [];
      const latestIntake = intakes.length > 0 ? intakes[0] : null;
      
      // Get labs
      const labs = labsRes.data || [];
      
      setPatientDetails(prev => ({
        ...prev,
        [appointment.id]: {
          documents: docs,
          insuranceFront,
          insuranceBack,
          licenseFront,
          licenseBack,
          medicalDocs,
          labs,
          consentStatus: consentRes.data || {},
          // Use intake data if available, fallback to appointment data
          intake: latestIntake,
          reasonForVisit: latestIntake?.chief_complaint || appointment.reason_for_visit,
          symptoms: latestIntake?.symptoms?.join(', ') || appointment.symptoms,
          vitals: latestIntake?.vital_signs || appointment.vitals,
          medications: latestIntake?.medications || [],
          allergies: latestIntake?.allergies || [],
          medicalHistory: latestIntake?.medical_history || [],
          labFilesCount: appointment.lab_files_count || 0
        }
      }));
    } catch (error) {
      console.error('Error loading patient details:', error);
      toast.error('Failed to load patient details');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Toggle expanded appointment
  const toggleAppointmentExpand = (appointment) => {
    if (expandedAppointment === appointment.id) {
      setExpandedAppointment(null);
    } else {
      setExpandedAppointment(appointment.id);
      // Load details if not already loaded
      if (!patientDetails[appointment.id]) {
        loadPatientDetails(appointment);
      }
    }
  };

  const loadProviders = useCallback(async () => {
    try {
      // /providers is accessible to all authenticated staff; /admin/users is admin-only
      const response = await adminAPI.getProviders();
      const providerList = (response.data || []).filter(u => u.role === 'provider');
      setProviders(providerList);
    } catch (error) {
      console.error('Error loading providers:', error);
    }
  }, []);

  const loadAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await appointmentAPI.getAll();
      
      // Filter by selected date
      const dayAppointments = (response.data || []).filter(apt => {
        const aptDate = new Date(apt.scheduled_time).toISOString().split('T')[0];
        return aptDate === selectedDate;
      });

      // Filter by provider if selected
      const filteredAppointments = selectedProvider === 'all' 
        ? dayAppointments 
        : dayAppointments.filter(apt => apt.provider_id === selectedProvider);

      // Sort by time
      filteredAppointments.sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));
      
      setAppointments(filteredAppointments);

      // Calculate stats
      setStats({
        total: filteredAppointments.length,
        scheduled: filteredAppointments.filter(a => a.status === 'scheduled').length,
        completed: filteredAppointments.filter(a => a.status === 'completed').length,
        cancelled: filteredAppointments.filter(a => a.status === 'cancelled').length,
        pending_changes: filteredAppointments.filter(a => a.pending_reschedule || a.pending_cancel).length
      });
    } catch (error) {
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedProvider]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  const changeDate = (days) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + days);
    setSelectedDate(current.toISOString().split('T')[0]);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getStatusBadge = (appointment) => {
    if (appointment.pending_cancel) {
      return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Cancel Requested</span>;
    }
    if (appointment.pending_reschedule) {
      return <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">Reschedule Requested</span>;
    }
    switch (appointment.status) {
      case 'scheduled':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">Scheduled</span>;
      case 'checked_in':
        return <span className="px-2 py-1 bg-teal-100 text-teal-700 text-xs rounded-full">Checked In</span>;
      case 'in_progress':
        return <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">In Progress</span>;
      case 'completed':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Completed</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-slate-100 text-slate-500 text-xs rounded-full">Cancelled</span>;
      case 'no_show':
        return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">No Show</span>;
      default:
        return <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">{appointment.status}</span>;
    }
  };

  const handleApproveReschedule = async (appointmentId) => {
    try {
      await appointmentAPI.approveReschedule(appointmentId);
      toast.success('Reschedule approved');
      loadAppointments();
    } catch (error) {
      toast.error('Failed to approve reschedule');
    }
  };

  const handleDenyReschedule = async (appointmentId) => {
    try {
      await appointmentAPI.denyReschedule(appointmentId);
      toast.success('Reschedule denied');
      loadAppointments();
    } catch (error) {
      toast.error('Failed to deny reschedule');
    }
  };

  const handleApproveCancel = async (appointmentId) => {
    try {
      await appointmentAPI.approveCancel(appointmentId);
      toast.success('Cancellation approved');
      loadAppointments();
    } catch (error) {
      toast.error('Failed to approve cancellation');
    }
  };

  const handleDenyCancel = async (appointmentId) => {
    try {
      await appointmentAPI.denyCancel(appointmentId);
      toast.success('Cancellation denied');
      loadAppointments();
    } catch (error) {
      toast.error('Failed to deny cancellation');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Daily Schedule</h1>
          <p className="text-slate-500">View and manage appointments for each day</p>
        </div>
      </div>

      {/* Date Navigation & Filters */}
      <div className="card-clinical p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Date Selector */}
          <div className="flex items-center gap-4">
            <button 
              onClick={() => changeDate(-1)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <CaretLeft size={20} className="text-slate-600" />
            </button>
            
            <div className="flex items-center gap-3">
              <CalendarIcon size={24} className="text-teal-600" />
              <div>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="text-lg font-semibold text-slate-900 bg-transparent border-none cursor-pointer"
                />
                <p className="text-sm text-slate-500">{formatDate(selectedDate)}</p>
              </div>
            </div>
            
            <button 
              onClick={() => changeDate(1)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <CaretRight size={20} className="text-slate-600" />
            </button>

            <button
              onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
              className="px-3 py-1 text-sm text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
            >
              Today
            </button>
          </div>

          {/* Provider Filter */}
          <div className="flex items-center gap-2">
            <Funnel size={18} className="text-slate-500" />
            <select
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
              className="input-clinical py-2"
              data-testid="provider-filter"
            >
              <option value="all">All Providers</option>
              {providers.map(provider => (
                <option key={provider.id} value={provider.id}>
                  {provider.first_name} {provider.last_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="card-clinical p-4 text-center">
          <Users size={24} className="text-slate-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
          <p className="text-sm text-slate-500">Total</p>
        </div>
        <div className="card-clinical p-4 text-center">
          <Clock size={24} className="text-blue-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-blue-600">{stats.scheduled}</p>
          <p className="text-sm text-slate-500">Scheduled</p>
        </div>
        <div className="card-clinical p-4 text-center">
          <CheckCircle size={24} className="text-green-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
          <p className="text-sm text-slate-500">Completed</p>
        </div>
        <div className="card-clinical p-4 text-center">
          <XCircle size={24} className="text-slate-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-slate-400">{stats.cancelled}</p>
          <p className="text-sm text-slate-500">Cancelled</p>
        </div>
        <div className="card-clinical p-4 text-center">
          <HourglassHigh size={24} className="text-amber-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-amber-600">{stats.pending_changes}</p>
          <p className="text-sm text-slate-500">Pending Requests</p>
        </div>
      </div>

      {/* Appointments List */}
      <div className="card-clinical overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h3 className="font-semibold text-slate-900">Appointments</h3>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="spinner mx-auto mb-4" />
            <p className="text-slate-500">Loading appointments...</p>
          </div>
        ) : appointments.length === 0 ? (
          <div className="p-8 text-center">
            <CalendarIcon size={48} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No appointments scheduled for this day</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {appointments.map((appointment) => (
              <div 
                key={appointment.id} 
                className={`transition-colors ${
                  appointment.pending_reschedule || appointment.pending_cancel ? 'bg-amber-50' : ''
                } ${
                  patientDetails[appointment.id]?.consentStatus?.any_expired ? 'border-l-4 border-l-red-500' : 
                  patientDetails[appointment.id]?.consentStatus?.any_expiring_soon ? 'border-l-4 border-l-amber-500' : ''
                }`}
                data-testid={`appointment-${appointment.id}`}
              >
                {/* Main appointment row - clickable */}
                <div 
                  className="p-4 hover:bg-slate-50 cursor-pointer"
                  onClick={() => toggleAppointmentExpand(appointment)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      {/* Expand indicator */}
                      <div className="mt-1">
                        {expandedAppointment === appointment.id ? (
                          <CaretUp size={18} className="text-teal-600" />
                        ) : (
                          <CaretDown size={18} className="text-slate-400" />
                        )}
                      </div>
                      
                      {/* Time */}
                      <div className="text-center min-w-[70px]">
                        <p className="text-lg font-semibold text-slate-900">{formatTime(appointment.scheduled_time)}</p>
                        <p className="text-xs text-slate-500">{appointment.duration_minutes || 30} min</p>
                      </div>

                      {/* Patient Info */}
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <User size={16} className="text-slate-400" />
                          <p className="font-medium text-slate-900">{appointment.patient_name}</p>
                          {getStatusBadge(appointment)}
                        </div>
                        
                        {/* Patient Contact Info - for CMA to confirm appointments */}
                        <div className="flex flex-wrap items-center gap-3 mt-2 mb-2">
                          {appointment.patient_phone && (
                            <a 
                              href={`tel:${appointment.patient_phone}`}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-teal-50 hover:bg-teal-100 text-teal-700 rounded-lg text-sm transition-colors"
                              onClick={(e) => e.stopPropagation()}
                              title="Call patient"
                            >
                              <Phone size={14} weight="fill" />
                              {appointment.patient_phone}
                            </a>
                          )}
                          {appointment.patient_email && (
                            <a 
                              href={`mailto:${appointment.patient_email}?subject=Appointment Confirmation - ${new Date(appointment.scheduled_time).toLocaleDateString()}`}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-sm transition-colors"
                              onClick={(e) => e.stopPropagation()}
                              title="Email patient"
                            >
                              <Envelope size={14} weight="fill" />
                              {appointment.patient_email}
                            </a>
                          )}
                        </div>
                        
                        <p className="text-sm text-slate-600 capitalize mb-1">
                          <Stethoscope size={14} className="inline mr-1" />
                          {appointment.appointment_type?.replace(/_/g, ' ')}
                        </p>
                        {appointment.provider_name && (
                          <p className="text-sm text-slate-500">
                            Provider: {appointment.provider_name}
                          </p>
                        )}
                        {appointment.notes && (
                          <p className="text-sm text-slate-500 mt-1 italic">
                            Note: {appointment.notes}
                          </p>
                        )}
                        
                        {/* Reschedule Request Info */}
                        {appointment.pending_reschedule && appointment.requested_time && (
                          <div className="mt-2 p-2 bg-amber-100 rounded-lg">
                            <p className="text-sm text-amber-800">
                              <strong>Requested new time:</strong> {new Date(appointment.requested_time).toLocaleString()}
                            </p>
                            {appointment.reschedule_reason && (
                              <p className="text-sm text-amber-700">Reason: {appointment.reschedule_reason}</p>
                            )}
                          </div>
                        )}

                        {/* Cancel Request Info */}
                        {appointment.pending_cancel && (
                          <div className="mt-2 p-2 bg-red-100 rounded-lg">
                            <p className="text-sm text-red-800">
                              <strong>Cancellation requested</strong>
                            </p>
                            {appointment.cancel_reason && (
                              <p className="text-sm text-red-700">Reason: {appointment.cancel_reason}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
                      {appointment.pending_reschedule && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApproveReschedule(appointment.id)}
                            className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-lg hover:bg-green-200"
                            data-testid={`approve-reschedule-${appointment.id}`}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleDenyReschedule(appointment.id)}
                            className="px-3 py-1 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200"
                            data-testid={`deny-reschedule-${appointment.id}`}
                          >
                            Deny
                          </button>
                        </div>
                      )}
                      {appointment.pending_cancel && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApproveCancel(appointment.id)}
                            className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-lg hover:bg-green-200"
                            data-testid={`approve-cancel-${appointment.id}`}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleDenyCancel(appointment.id)}
                            className="px-3 py-1 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200"
                            data-testid={`deny-cancel-${appointment.id}`}
                          >
                            Deny
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Patient Details Panel */}
                {expandedAppointment === appointment.id && (
                  <div className="px-4 pb-4 bg-slate-50 border-t border-slate-200">
                    {loadingDetails ? (
                      <div className="py-6 text-center">
                        <div className="spinner mx-auto mb-2" />
                        <p className="text-sm text-slate-500">Loading patient details...</p>
                      </div>
                    ) : patientDetails[appointment.id] ? (
                      <div className="pt-4 space-y-4">
                        {/* Patient Review Header */}
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                            <ClipboardText size={18} className="text-teal-600" />
                            Patient Chart Review
                          </h4>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/staff/patients/${appointment.patient_id}/chart`);
                            }}
                            className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1"
                          >
                            View Full Chart <ArrowRight size={14} />
                          </button>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {/* Insurance Card Status */}
                          <div className="bg-white rounded-xl p-4 border border-slate-200">
                            <h5 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
                              <CreditCard size={16} className="text-blue-600" />
                              Insurance Card
                            </h5>
                            {patientDetails[appointment.id].insuranceFront ? (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm">
                                  <CheckCircle size={16} className="text-green-600" />
                                  <span className="text-green-700">Front uploaded</span>
                                </div>
                                {patientDetails[appointment.id].insuranceBack ? (
                                  <div className="flex items-center gap-2 text-sm">
                                    <CheckCircle size={16} className="text-green-600" />
                                    <span className="text-green-700">Back uploaded</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 text-sm">
                                    <Warning size={16} className="text-amber-600" />
                                    <span className="text-amber-700">Back missing</span>
                                  </div>
                                )}
                                {patientDetails[appointment.id].insuranceFront.expiration_date && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    Expires: {new Date(patientDetails[appointment.id].insuranceFront.expiration_date).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-sm">
                                <Warning size={16} className="text-red-600" />
                                <span className="text-red-700">Not uploaded</span>
                              </div>
                            )}
                          </div>

                          {/* ID Card Status */}
                          <div className="bg-white rounded-xl p-4 border border-slate-200">
                            <h5 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
                              <IdentificationCard size={16} className="text-purple-600" />
                              Photo ID
                            </h5>
                            {patientDetails[appointment.id].licenseFront ? (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm">
                                  <CheckCircle size={16} className="text-green-600" />
                                  <span className="text-green-700">Front uploaded</span>
                                </div>
                                {patientDetails[appointment.id].licenseBack ? (
                                  <div className="flex items-center gap-2 text-sm">
                                    <CheckCircle size={16} className="text-green-600" />
                                    <span className="text-green-700">Back uploaded</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 text-sm">
                                    <Warning size={16} className="text-amber-600" />
                                    <span className="text-amber-700">Back missing</span>
                                  </div>
                                )}
                                {patientDetails[appointment.id].licenseFront.expiration_date && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    Expires: {new Date(patientDetails[appointment.id].licenseFront.expiration_date).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-sm">
                                <Warning size={16} className="text-red-600" />
                                <span className="text-red-700">Not uploaded</span>
                              </div>
                            )}
                          </div>

                          {/* Consent Status */}
                          <div className="bg-white rounded-xl p-4 border border-slate-200">
                            <h5 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
                              <Shield size={16} className="text-teal-600" />
                              HIPAA Consents
                              {/* AI Flag for expired/expiring consents */}
                              {(patientDetails[appointment.id].consentStatus?.any_expired || 
                                patientDetails[appointment.id].consentStatus?.any_expiring_soon) && (
                                <span className={`ml-auto px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${
                                  patientDetails[appointment.id].consentStatus?.any_expired 
                                    ? 'bg-red-100 text-red-700' 
                                    : 'bg-amber-100 text-amber-700'
                                }`}>
                                  <Warning size={12} />
                                  {patientDetails[appointment.id].consentStatus?.any_expired ? 'EXPIRED' : 'EXPIRING'}
                                </span>
                              )}
                            </h5>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm">
                                {patientDetails[appointment.id].consentStatus?.privacy_notice ? (
                                  <>
                                    <CheckCircle size={16} className="text-green-600" />
                                    <span className="text-green-700">Privacy Notice</span>
                                    {patientDetails[appointment.id].consentStatus?.privacy_notice_expires && (
                                      <span className="text-xs text-slate-400 ml-auto">
                                        Exp: {new Date(patientDetails[appointment.id].consentStatus.privacy_notice_expires).toLocaleDateString()}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <Warning size={16} className="text-red-600" />
                                    <span className="text-red-700">Privacy Notice - Missing/Expired</span>
                                  </>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                {patientDetails[appointment.id].consentStatus?.treatment_consent ? (
                                  <>
                                    <CheckCircle size={16} className="text-green-600" />
                                    <span className="text-green-700">Treatment Consent</span>
                                    {patientDetails[appointment.id].consentStatus?.treatment_consent_expires && (
                                      <span className="text-xs text-slate-400 ml-auto">
                                        Exp: {new Date(patientDetails[appointment.id].consentStatus.treatment_consent_expires).toLocaleDateString()}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <Warning size={16} className="text-red-600" />
                                    <span className="text-red-700">Treatment Consent - Missing/Expired</span>
                                  </>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                {patientDetails[appointment.id].consentStatus?.financial_responsibility ? (
                                  <>
                                    <CheckCircle size={16} className="text-green-600" />
                                    <span className="text-green-700">Financial Responsibility</span>
                                    {patientDetails[appointment.id].consentStatus?.financial_responsibility_expires && (
                                      <span className="text-xs text-slate-400 ml-auto">
                                        Exp: {new Date(patientDetails[appointment.id].consentStatus.financial_responsibility_expires).toLocaleDateString()}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <Warning size={16} className="text-red-600" />
                                    <span className="text-red-700">Financial Responsibility - Missing/Expired</span>
                                  </>
                                )}
                              </div>
                            </div>
                            {/* AI Recommendation for consent renewal */}
                            {(patientDetails[appointment.id].consentStatus?.any_expired || 
                              patientDetails[appointment.id].consentStatus?.any_expiring_soon) && (
                              <div className={`mt-3 p-2 rounded-lg text-xs ${
                                patientDetails[appointment.id].consentStatus?.any_expired 
                                  ? 'bg-red-50 text-red-700' 
                                  : 'bg-amber-50 text-amber-700'
                              }`}>
                                <strong>AI Recommendation:</strong> {patientDetails[appointment.id].consentStatus?.any_expired 
                                  ? 'Patient must sign new HIPAA consents before encounter can proceed.'
                                  : 'Consider having patient renew consents during this visit to avoid expiration.'}
                              </div>
                            )}
                          </div>

                          {/* Health Intake */}
                          <div className="bg-white rounded-xl p-4 border border-slate-200">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                                <FirstAid size={16} className="text-red-600" />
                                Patient Complaint
                              </h5>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/cma/chart/${appointment.patient_id}/${appointment.id}`);
                                }}
                                className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-lg hover:bg-teal-200 flex items-center gap-1"
                                data-testid={`edit-chart-${appointment.id}`}
                              >
                                <ClipboardText size={12} />
                                Open/Edit Chart
                              </button>
                            </div>
                            {patientDetails[appointment.id].reasonForVisit ? (
                              <div className="space-y-2">
                                <div>
                                  <p className="text-xs text-slate-500">Chief Complaint</p>
                                  <p className="text-sm text-slate-800">{patientDetails[appointment.id].reasonForVisit}</p>
                                </div>
                                {patientDetails[appointment.id].symptoms && (
                                  <div>
                                    <p className="text-xs text-slate-500">Symptoms</p>
                                    <p className="text-sm text-slate-800">{patientDetails[appointment.id].symptoms}</p>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-sm">
                                <Warning size={16} className="text-amber-600" />
                                <span className="text-amber-700">Not completed</span>
                              </div>
                            )}
                          </div>

                          {/* Vitals */}
                          <div className="bg-white rounded-xl p-4 border border-slate-200">
                            <h5 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
                              <Heart size={16} className="text-pink-600" />
                              Patient-Reported Vitals
                            </h5>
                            {patientDetails[appointment.id].vitals && (patientDetails[appointment.id].vitals.blood_pressure || patientDetails[appointment.id].vitals.heart_rate || patientDetails[appointment.id].vitals.temperature) ? (
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                {patientDetails[appointment.id].vitals.blood_pressure && (
                                  <div>
                                    <p className="text-xs text-slate-500">BP</p>
                                    <p className="text-slate-800">{patientDetails[appointment.id].vitals.blood_pressure}</p>
                                  </div>
                                )}
                                {patientDetails[appointment.id].vitals.heart_rate && (
                                  <div>
                                    <p className="text-xs text-slate-500">HR</p>
                                    <p className="text-slate-800">{patientDetails[appointment.id].vitals.heart_rate} bpm</p>
                                  </div>
                                )}
                                {patientDetails[appointment.id].vitals.temperature && (
                                  <div>
                                    <p className="text-xs text-slate-500">Temp</p>
                                    <p className="text-slate-800">{patientDetails[appointment.id].vitals.temperature}°F</p>
                                  </div>
                                )}
                                {patientDetails[appointment.id].vitals.weight && (
                                  <div>
                                    <p className="text-xs text-slate-500">Weight</p>
                                    <p className="text-slate-800">{patientDetails[appointment.id].vitals.weight} lbs</p>
                                  </div>
                                )}
                                {patientDetails[appointment.id].vitals.oxygen_saturation && (
                                  <div>
                                    <p className="text-xs text-slate-500">O2 Sat</p>
                                    <p className="text-slate-800">{patientDetails[appointment.id].vitals.oxygen_saturation}%</p>
                                  </div>
                                )}
                                {patientDetails[appointment.id].vitals.respiratory_rate && (
                                  <div>
                                    <p className="text-xs text-slate-500">Resp Rate</p>
                                    <p className="text-slate-800">{patientDetails[appointment.id].vitals.respiratory_rate}/min</p>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-slate-500">No vitals reported</span>
                              </div>
                            )}
                          </div>

                          {/* Labs & Medical Documents */}
                          <div className="bg-white rounded-xl p-4 border border-slate-200">
                            <h5 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
                              <Flask size={16} className="text-indigo-600" />
                              Labs & Medical Documents
                            </h5>
                            
                            {/* Lab Results */}
                            {patientDetails[appointment.id].labs?.length > 0 && (
                              <div className="mb-3">
                                <p className="text-xs text-slate-500 mb-2">Lab Results</p>
                                <div className="space-y-2">
                                  {patientDetails[appointment.id].labs.slice(0, 3).map((lab, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-2 bg-indigo-50 rounded-lg">
                                      <div className="flex items-center gap-2">
                                        <FileText size={14} className="text-indigo-600" />
                                        <div>
                                          <p className="text-sm font-medium text-slate-800">{lab.test_name || 'Lab Result'}</p>
                                          <p className="text-xs text-slate-500">{new Date(lab.created_at).toLocaleDateString()}</p>
                                        </div>
                                      </div>
                                      <button 
                                        onClick={(e) => { e.stopPropagation(); navigate(`/staff/labs/${lab.id}`); }}
                                        className="text-indigo-600 hover:text-indigo-800"
                                      >
                                        <ArrowRight size={16} />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Uploaded Medical Documents (X-rays, MRIs, etc.) */}
                            {patientDetails[appointment.id].medicalDocs?.length > 0 ? (
                              <div>
                                <p className="text-xs text-slate-500 mb-2">Uploaded Files</p>
                                <div className="space-y-2">
                                  {patientDetails[appointment.id].medicalDocs.map((doc, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                                      <div className="flex items-center gap-2">
                                        {doc.mime_type?.includes('image') ? (
                                          <Image size={14} className="text-green-600" />
                                        ) : doc.mime_type?.includes('pdf') ? (
                                          <FilePdf size={14} className="text-red-600" />
                                        ) : (
                                          <File size={14} className="text-slate-600" />
                                        )}
                                        <div>
                                          <p className="text-sm font-medium text-slate-800 capitalize">
                                            {doc.document_type?.replace(/_/g, ' ') || doc.file_name}
                                          </p>
                                          <p className="text-xs text-slate-500">
                                            {doc.file_name} • {(doc.file_size / 1024).toFixed(1)}KB
                                          </p>
                                        </div>
                                      </div>
                                      <a 
                                        href={`data:${doc.mime_type};base64,${doc.file_data}`}
                                        download={doc.file_name}
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-teal-600 hover:text-teal-800"
                                      >
                                        <DownloadSimple size={16} />
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              !patientDetails[appointment.id].labs?.length && (
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="text-slate-500">No labs or documents uploaded</span>
                                </div>
                              )
                            )}
                          </div>

                          {/* Telehealth Video */}
                          <div className="bg-white rounded-xl p-4 border border-slate-200">
                            <h5 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
                              <VideoCamera size={16} className="text-purple-600" />
                              Telehealth Video
                            </h5>
                            {appointment.meeting_room_name ? (
                              <div className="space-y-3">
                                <div className="flex items-center gap-2 text-sm">
                                  <CheckCircle size={16} className="text-green-600" />
                                  <span className="text-green-700">Video room ready</span>
                                </div>
                                <p className="text-xs text-slate-500">
                                  Room: {appointment.meeting_room_name}
                                </p>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Open video call page
                                    window.open(`/staff/video-call/${appointment.id}`, '_blank');
                                  }}
                                  className="w-full px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2"
                                  data-testid={`join-video-${appointment.id}`}
                                >
                                  <VideoCamera size={16} />
                                  Join Video Call
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-sm">
                                <Warning size={16} className="text-amber-600" />
                                <span className="text-amber-700">No video room assigned</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/staff/patients/${appointment.patient_id}/chart`);
                            }}
                            className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 flex items-center gap-2"
                            data-testid={`view-chart-${appointment.id}`}
                          >
                            <ClipboardText size={16} />
                            Full Patient Chart
                          </button>
                          {appointment.status === 'checked_in' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/cma/triage/${appointment.id}`);
                              }}
                              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-2"
                              data-testid={`start-triage-${appointment.id}`}
                            >
                              <Pill size={16} />
                              Start Triage
                            </button>
                          )}
                          {appointment.meeting_room_name && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`/staff/video-call/${appointment.id}`, '_blank');
                              }}
                              className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 flex items-center gap-2"
                              data-testid={`video-call-${appointment.id}`}
                            >
                              <VideoCamera size={16} />
                              Video Call
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="py-4 text-center text-sm text-slate-500">
                        Click to load patient details
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
